/**
 * Errors raised by code we do not ship (DHT-5531): browser extensions,
 * cross-origin scripts ("Script error.") and a tenant's own Google Tag Manager
 * tags. None of them is actionable, and together they were the largest block of
 * noise in web RUM.
 *
 * Frames are found by URL token rather than by line shape, because the stack
 * can arrive as the browser SDK formats it (`  at fn @ url:1:2`), as raw V8
 * (`at fn (url:1:2)`) or as Safari/Firefox (`fn@url:1:2`). Lines with no URL
 * (`[native code]`, `<anonymous>`, the `Error: message` header, the SDK's
 * "No stack" placeholder) contribute no frame.
 *
 * DHT-5554 added three narrower rules: Microsoft's email link scanner, console
 * errors raised by a tenant's own tracking script (whose frames are only in
 * `error.handling_stack`), and failed RUM uploads surfacing through an
 * extension's `fetch` wrapper.
 *
 * DHT-5623 added two more: an uncaught error whose stack has a web frame but
 * none from our bundle (iOS-injected scripts running at the page URL, hCaptcha's
 * own api.js, blob workers), and an uncaught AbortError, which is a request
 * someone cancelled rather than a failure.
 */

const FRAME_URL = /[a-z][a-z0-9+.-]*:\/\/[^\s()]+/gi;

const EXTENSION_SCHEMES = [
  "chrome-extension://",
  "moz-extension://",
  "safari-web-extension://",
];

// Our bundle is served from /static/js/ on every web host, tenant custom
// domains included. Only http(s), so an extension's own /static/js/ is not ours.
const FIRST_PARTY = /^https?:\/\/[^/]+\/static\/js\//i;

const SCRIPT_ERROR = /^(Uncaught )?"?Script error\.?"?$/;

const GTM_SCRIPT = "googletagmanager.com/gtm.js";

// Microsoft's link scanner opening links in clinic emails (DHT-5554). Never a
// signed-in user, never a real stack.
const LINK_SCANNER =
  /^(Uncaught )?"?Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+"?$/;

const FAILED_TO_FETCH = "Failed to fetch";

const WEB_URL = /^https?:\/\//i;

// The browsers' wordings of an aborted fetch, as RUM records them uncaught.
const ABORT_MESSAGE =
  /^(Uncaught )?"?(signal is aborted without reason|The (user|operation) aborted a request\.?|AbortError(?![A-Za-z]))/;

// The message is cut out first, so a URL quoted in the "Error: message" header
// line is not mistaken for a frame.
const frameUrls = (stack: unknown, message?: unknown): string[] => {
  if (typeof stack !== "string") return [];
  const frames =
    typeof message === "string" && message ? stack.replace(message, "") : stack;
  return frames.match(FRAME_URL) ?? [];
};

const isExtensionUrl = (url: string) =>
  EXTENSION_SCHEMES.some(scheme => url.toLowerCase().startsWith(scheme));

const isFirstPartyUrl = (url: string) => FIRST_PARTY.test(url);

/**
 * True when an error event matches one of the third-party rules and
 * should be dropped. Never throws: anything it cannot read is kept, because
 * losing a real error is worse than keeping noise, and a throw inside
 * `beforeSend` would break every event.
 */
export const isThirdPartyError = (event: unknown): boolean => {
  try {
    const error = (
      event as {
        error?: {
          message?: unknown;
          stack?: unknown;
          source?: unknown;
          type?: unknown;
          handling_stack?: unknown;
          handling?: unknown;
        };
      }
    )?.error;
    if (!error || typeof error !== "object") return false;

    // Our own Apollo client raised it, so no rule below may drop it, whatever
    // an extension or tag wrapped around the request (DHT-5554 AC6).
    if (error.type === "ApolloError") return false;

    const urls = frameUrls(error.stack, error.message);
    const message =
      typeof error.message === "string" ? error.message.trim() : undefined;

    // At least one frame, and every frame is an extension's.
    if (urls.length > 0 && urls.every(isExtensionUrl)) return true;

    // A cross-origin script the browser has stripped of all detail.
    if (
      urls.length === 0 &&
      typeof error.message === "string" &&
      SCRIPT_ERROR.test(error.message.trim())
    ) {
      return true;
    }

    // A tenant GTM tag. The top frame is often the page itself (an inline
    // script gtm.js injected), so match any gtm.js frame, and keep the event
    // if any frame is our own bundle.
    if (
      urls.some(url => url.includes(GTM_SCRIPT)) &&
      !urls.some(isFirstPartyUrl)
    ) {
      return true;
    }

    // Microsoft's link scanner: an uncaught error with no frame at all.
    if (
      error.source === "source" &&
      urls.length === 0 &&
      message !== undefined &&
      LINK_SCANNER.test(message)
    ) {
      return true;
    }

    // A console error logged by a script we do not ship. Console errors carry
    // their frames in handling_stack, not stack. One with no URL frame (our own
    // console.error once the SDK has trimmed its frames) is kept, as is one
    // with our bundle anywhere in either stack.
    if (error.source === "console") {
      const handlingUrls = frameUrls(error.handling_stack);
      if (
        handlingUrls.length > 0 &&
        !handlingUrls.some(isFirstPartyUrl) &&
        !urls.some(isFirstPartyUrl)
      ) {
        return true;
      }
    }

    // A failed fetch that an extension's fetch wrapper threw. The frames below
    // the extension's are the RUM SDK's own upload path, so only the first frame
    // decides. ApolloError has a different type and is always kept.
    if (
      error.type === "TypeError" &&
      message === FAILED_TO_FETCH &&
      urls.length > 0 &&
      isExtensionUrl(urls[0])
    ) {
      return true;
    }

    // An uncaught error with at least one web frame and none from our bundle:
    // a script we do not ship threw it (DHT-5623). Frameless stacks are left
    // to the rules above, and console, custom and network errors never reach
    // this rule. `handling` is not consulted: a "source" error is uncaught. A blob worker's frame (`blob:https://host/uuid`) reads as a
    // web URL that is not ours, so it is dropped here too.
    if (
      error.source === "source" &&
      urls.some(url => WEB_URL.test(url)) &&
      !urls.some(isFirstPartyUrl)
    ) {
      return true;
    }

    // An uncaught AbortError is a cancelled request, not a failure. One our
    // code catches and logs arrives as handled, and is kept.
    if (
      error.handling === "unhandled" &&
      (error.type === "AbortError" ||
        (message !== undefined && ABORT_MESSAGE.test(message)))
    ) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};
